import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { CATEGORY_TINT, FLOW_TINTS, type FlowCategory } from './flows-styles';

export type TemplateCardNode = {
  category: FlowCategory;
  icon: ElementType<{ className?: string }>;
};

export type TemplateCardProps = Omit<ComponentProps<'button'>, 'title' | 'children'> & {
  /** Figma "Node strip": the template's first nodes; up to 4 are shown. */
  nodes: readonly TemplateCardNode[];
  /** Figma "Title". */
  title: ReactNode;
  /** Figma "Description". */
  description: ReactNode;
  /** Figma "Meta": e.g. "9 nodes · most popular". */
  meta?: ReactNode;
};

/**
 * Starter template on the Flows index (Figma ↳ Flows ↳ Template card, 153:98):
 * bg/surface, 1px border/subtle, radius/xl, 20px padding, gap 12. The node strip
 * overlaps 32px radius/md minis (-6px) with a 1.5px stroke and a 16px icon in
 * the category colour. Heading/H4 title, Body/Small text/secondary description,
 * Caption text/tertiary meta. The whole card is the button.
 */
export const templateCardStyles = tv({
  slots: {
    root: [
      'bg-bg-surface border-border-subtle flex w-full cursor-pointer flex-col items-start gap-3 rounded-xl border p-5 text-left outline-none',
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
      'hover:border-border-strong focus-visible:shadow-focus-ring',
      'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border-subtle',
    ],
    strip: 'flex items-start',
    mini: 'bg-bg-surface -mr-1.5 flex size-8 shrink-0 items-center justify-center rounded-md inset-ring-[1.5px] last:mr-0',
    icon: 'size-4 shrink-0',
    title: 'text-heading-h4 text-text-primary',
    description: 'text-body-sm text-text-secondary',
    meta: 'text-caption text-text-tertiary',
  },
});

export function TemplateCard({
  nodes,
  title,
  description,
  meta,
  className,
  type = 'button',
  ...props
}: TemplateCardProps) {
  const styles = templateCardStyles();
  return (
    <button
      type={type}
      data-slot="template-card"
      className={styles.root({ className })}
      {...props}
    >
      <span aria-hidden data-slot="template-card-strip" className={styles.strip()}>
        {nodes.slice(0, 4).map(({ category, icon: Icon }, i) => {
          const tint = FLOW_TINTS[CATEGORY_TINT[category]];
          return (
            <span
              key={i}
              data-slot="template-card-mini"
              className={styles.mini({ className: tint.ring })}
            >
              <Icon className={styles.icon({ className: tint.text })} />
            </span>
          );
        })}
      </span>
      <span data-slot="template-card-title" className={styles.title()}>
        {title}
      </span>
      <span data-slot="template-card-description" className={styles.description()}>
        {description}
      </span>
      {meta && (
        <span data-slot="template-card-meta" className={styles.meta()}>
          {meta}
        </span>
      )}
    </button>
  );
}
